import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { userState } from "../../../recoil/auth";
import { cafeDetail } from "../../../api/cafe";
import { Cafe, ReservationResponse } from "../../../api/types";
import useScreenOrientation from "../../../hooks/useScreenOrientation";
import { isApp } from "../../../hooks/useNativeApp";
import { enqueueSnackbar } from "notistack";
import {
  Container,
  Label,
  PubContainer,
  ReservationButton,
  ReservationInfoButton,
  TournamentImage,
  TournamentImageWrapper,
  ValueLabel,
  WarningWrapper,
} from "./style";
import ImageViewerModal from "../../../components/common/ImageViewerModal";
import { shareURL } from "../../../utils/common";
import ReservationStatusPopup from "./component/ReservationStatusPopup";
import CompanyReservationPopup from "./component/ReservationPopup";
import OpenApp from "./component/OpenApp";
import CompanyReservationEnd from "./component/ReservationEndPopup";
import { Reservation } from "./types";
import {
  getCompanyCurrentReservations,
  getCompanyReservation,
} from "../../../api/company-reservation";
import {
  ButtonWrapper,
  ReservationContainer,
  ReservationHeader,
} from "../ReservationLandingPage/index.styles";
import { insetState } from "../../../recoil/inset";

const CompanyReservation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [user] = useRecoilState(userState);
  const history = useHistory();
  const [isReservationPopupOpen, setIsReservationPopupOpen] = useState(false);
  const [cafe, setCafe] = useState<Cafe | null>(null);
  const [reservation, setReservation] = useState<ReservationResponse | null>(
    null
  );
  const [isStatusPopupOpen, setIsStatusPopupOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [type, setType] = useState<string>("");
  const [isOpenUseApp, setIsOpenUseApp] = useState(false);
  const [isFinalReservation, setIsFinalReservation] = useState(false);
  const [totalReservations, setTotalReservations] = useState(0);
  const [reservationId, setReservationId] = useState<number>(0);
  const [keepSeed, setKeepSeed] = useState(0);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [isReservation, setIsReservation] = useState<boolean>(false);
  const [isSpecial, setIsSpecial] = useState<boolean>(false);
  const [isEnd, setIsEnd] = useState<boolean>(false);
  const [isThreeDaysBefore, setIsThreeDaysBefore] = useState<boolean>(false);
  const [dDay, setDDay] = useState<number>(0);
  const inset = useRecoilValue(insetState);

  const orientation = useScreenOrientation();
  useEffect(() => {
    getData();
  }, [id]);

  const getData = async () => {
    if (!id) return;
    getCompanyReservation(id).then((reservation) => {
      setReservation(reservation);
      cafeDetail(reservation.pubId).then(setCafe);
      setKeepSeed(reservation.keepSeed);
      setIsEnd(reservation.isEnd);
      if (
        reservation.limitEntry &&
        reservation.reservationCount === reservation.limitEntry
      ) {
        setIsEnd(true);
      }
      const startDate = new Date(
        reservation?.tournamentStart ? reservation.tournamentStart : ""
      );
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      const startDateOnly = new Date(startDate);
      startDateOnly.setHours(0, 0, 0, 0);

      const dDayRemaining = Math.ceil(
        (startDateOnly.getTime() - currentDate.getTime()) /
          (1000 * 60 * 60 * 24)
      );
      setDDay(dDayRemaining);
      if (dDayRemaining <= 3) {
        setIsThreeDaysBefore(true);
      }
    });

    getCompanyCurrentReservations(+id).then((response) => {
      setTotalReservations(response.length);
      setReservations(
        response.map((item, index) => {
          return {
            order: response.length - index + "",
            customerName: item.nickname,
            id: item.id,
            route: item.route,
          };
        })
      );
    });
  };

  const handelReservationCount = () => {
    if (!reservation) return `${totalReservations}명`;
    if (reservation.limitEntry) {
      return `${reservation.reservationCount}/${reservation.limitEntry}명`;
    }
    return `${reservation.reservationCount}명`;
  };

  useEffect(() => {
    if (!user) return;

    const isReservation = reservations.some(
      (item) => item.customerName === user.nickname
    );
    setIsReservation(isReservation);
    if (isReservation) {
      const reservation = reservations.find(
        (item) => item.customerName === user.nickname
      );
      if (reservation) {
        setReservationId(reservation.id);
        setIsSpecial(reservation.route === "SPECIAL");
      }
    }
  }, [reservations, user]);

  useEffect(() => {
    if (!isApp && orientation !== "landscape") {
      setIsOpenUseApp(true);
    }
  }, []);

  const handleClose = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push("/");
    }
  };

  const goToApp = () => {
    if (!isApp) {
      const isKakao = /KAKAOTALK/i.test(navigator.userAgent);
      const redirectUrl =
        window.location.href +
        (window.location.search ? "&" : "?") +
        "store_redirect=true";
      // 앱 실행 시도
      if (isKakao) {
        window.location.href =
          "kakaotalk://web/openExternal?url=" + encodeURIComponent(redirectUrl);
      } else {
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
        if (isIOS) {
          const link = document.createElement("a");
          link.href = redirectUrl;
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          window.open(redirectUrl, "_blank");
          window.close();
        }
      }
      return;
    }
  };

  useEffect(() => {
    // URL에서 store_redirect 파라미터 확인
    const params = new URLSearchParams(window.location.search);
    const shouldRedirect = params.get("store_redirect");

    if (shouldRedirect === "true" && !isApp) {
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isIOS) {
        window.location.href = "https://apps.apple.com/app/6469495456";
      } else {
        window.location.href =
          "https://play.google.com/store/apps/details?id=com.runnersoft.runnerrunner";
      }
    }
  }, []);

  const handleReservationClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!user) {
      enqueueSnackbar("로그인이 필요합니다. ");
      history.push("/login");
      return;
    }
    if (!user.validate) {
      enqueueSnackbar("본인인증 후 예약하실 수 있습니다. ");
      return;
    }
    if (isSpecial) {
      enqueueSnackbar("특별초대권으로 예약된 토너먼트는 취소할 수 없습니다. ");
      return;
    }
    if (isReservation) {
      if (isThreeDaysBefore) {
        enqueueSnackbar("대회 시작 3일 전에는 예약을 취소할 수 없습니다. ");
        return;
      }
      setType("cancel");
    } else {
      if (isEnd) {
        enqueueSnackbar("예약이 마감되었습니다. ");
        return;
      }
      setType("reservation");
    }
    setIsReservationPopupOpen(true);
  };

  const getDayText = () => {
    if (dDay === 0) {
      return "D-Day";
    } else if (dDay < 0) {
      return "예약 마감";
    } else {
      return "D-" + dDay;
    }
  };

  const getButtonText = () => {
    if (isReservation) {
      if (isSpecial) {
        return "특별초대권 예약취소 불가";
      }
      if (isThreeDaysBefore) {
        return getDayText();
      }
      return "예약 취소";
    }
    if (isEnd) {
      return "예약 마감";
    }
    return "토너먼트 예약하기";
  };

  return (
    <Container>
      {reservation && (
        <ReservationContainer isApp={inset}>
          <ReservationHeader isApp={inset}>
            <div className="left">
              <div className="close" onClick={handleClose}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 19L5 12M5 12L12 5M5 12H19"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="title">
                {cafe?.cafeName ? cafe.cafeName : "예약"}
              </div>
            </div>
            <div
              className="share"
              onClick={() => shareURL(window.location.href, reservation.title)}
            >
              <img
                src="/image-web/reservation/new_share_button.svg"
                alt="To Map"
              />
            </div>
          </ReservationHeader>
          <TournamentImageWrapper>
            <TournamentImage
              alt="토너먼트"
              src={reservation.imageUrl}
              onClick={() => setSelectedImage(reservation.imageUrl)}
            ></TournamentImage>
          </TournamentImageWrapper>
          <div className="title">{reservation.title}</div>
          {cafe && (
            <PubContainer>
              <div className="row">
                <Label>매장이름</Label>
                <ValueLabel>{cafe.cafeName}</ValueLabel>
              </div>
              <div className="row">
                <Label>주소</Label>
                <ValueLabel>
                  {reservation.address || cafe.newAddress}
                </ValueLabel>
              </div>
              <div className="row">
                <Label>매장번호</Label>
                <ValueLabel>{cafe.vcn}</ValueLabel>
              </div>
            </PubContainer>
          )}
          <WarningWrapper>
            ※불가피하게 예약 취소를 원하시는 경우 매장 으로 전화 부탁 드립니다.
          </WarningWrapper>
          <ButtonWrapper>
            <div
              className="state-button"
              onClick={() => setIsStatusPopupOpen(true)}
            >
              <img src="/image-web/reservation/state_button.svg" alt="To Map" />
              <div className="state">{handelReservationCount()}</div>
            </div>
            <div
              className={`button ${
                isReservation && !isThreeDaysBefore ? "cancel" : ""
              }`}
              onClick={(e: any) => {
                handleReservationClick(e);
              }}
            >
              {getButtonText()}
            </div>
          </ButtonWrapper>
        </ReservationContainer>
      )}

      <ReservationStatusPopup
        isOpen={isStatusPopupOpen}
        onClose={() => setIsStatusPopupOpen(false)}
        reservations={reservations}
      />
      {isReservationPopupOpen && (
        <CompanyReservationPopup
          onClose={() => setIsReservationPopupOpen(false)}
          reservationId={+id || 0}
          setIsFinalReservation={setIsFinalReservation}
          keepSeed={keepSeed}
          type={type}
          tournamentName={reservation?.title || ""}
          id={reservationId}
        />
      )}
      <ImageViewerModal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageUrl={selectedImage || ""}
      ></ImageViewerModal>
      {isOpenUseApp && (
        <OpenApp
          openApp={() => goToApp()}
          onClose={() => setIsOpenUseApp(false)}
        />
      )}
      {isFinalReservation && (
        <CompanyReservationEnd
          onClose={() => {
            setIsFinalReservation(false);
            getData();
          }}
          tournamentTitle={reservation?.title || ""}
          keepSeed={keepSeed}
          type={type}
        />
      )}
    </Container>
  );
};

export default CompanyReservation;
